/**
 * Button style resolver
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ISchemaConstObject, TGetBlueprintSchemaSpec } from "@adapptio/lib-blueprint";
import { IButtonStyleProps, BUTTON_STYLE_string } from "@adapptio/lib-components";
import { ComponentButton_Props } from "./props";

/**
 * Resolved button style
 */
export interface IResolvedButtonStyle extends IButtonStyleProps {
	style: BUTTON_STYLE_string;
}

/**
 * Resolves button style
 *
 * @param styleData Style data
 */
export function resolveButtonStyle(
	styleData: TGetBlueprintSchemaSpec<ISchemaConstObject<typeof ComponentButton_Props>>["styleData"]
): IResolvedButtonStyle {
	const style = styleData.type as BUTTON_STYLE_string;
	const styleDataValue = styleData.value[style];

	const result: IResolvedButtonStyle = {
		style
	};

	Object.entries(styleDataValue || {}).forEach(([ key, value ]) => {
		if (key !== "hoverState" && key !== "activeState" && value) {
			result[key] = value;
		}
	});

	if (styleDataValue?.hoverState) {
		Object.entries(styleDataValue.hoverState).forEach(([ key, value ]) => {
			if (value) {
				result[`hover${key.charAt(0).toUpperCase()}${key.slice(1)}`] = value;
			}
		});
	}

	if (styleDataValue?.activeState) {
		Object.entries(styleDataValue.activeState).forEach(([ key, value ]) => {
			if (value) {
				result[`active${key.charAt(0).toUpperCase()}${key.slice(1)}`] = value;
			}
		});
	}

	return result;
}
